/**
 * Stream Recovery
 *
 * Handles socket disconnect/reconnect during active conversation streaming.
 * On disconnect: stalls the stream silently (no error UI — the banner handles it).
 * On reconnect: auto-retries the interrupted AI response if still on the same session.
 *
 * Extracted from conversationStore for testability — all dependencies are injected.
 */

import type { MessageStatus } from '../types/conversation';
import { createLogger } from '../utils/logger';

const log = createLogger('StreamRecovery');

/** Minimal interface for reading conversation state */
export interface ConversationSnapshot {
  isStreaming: boolean;
  currentSessionId: string | null;
  messages: Array<{ id: string; role?: string; status: string; text?: string }>;
  streamingText: string;
}

/** Actions the recovery handler can trigger on the conversation store */
export interface ConversationActions {
  setState: (updater: (s: ConversationSnapshot & { streamingMessageId: string | null }) => Record<string, unknown>) => void;
  getState: () => ConversationSnapshot & { streamingMessageId: string | null };
  retryConversation: (messageId: string) => Promise<void>;
}

/** Actions the recovery handler can trigger on the TTS store */
export interface TTSActions {
  cancel: () => void;
}

/** Remembered state from an interrupted stream */
export interface InterruptedStream {
  aiMessageId: string;
  sessionId: string;
}

/**
 * Create the stream recovery handler.
 * Returns the disconnect/reconnect callbacks and a getter for the interrupted state (for testing).
 */
export function createStreamRecovery(deps: {
  conversation: ConversationActions;
  tts: TTSActions;
  scheduleRetry?: (fn: () => void) => void;
}) {
  const { conversation, tts } = deps;
  const scheduleRetry = deps.scheduleRetry ?? ((fn) => setTimeout(fn, 500));

  let interruptedStream: InterruptedStream | null = null;

  /** Called when socket transitions from connected → disconnected */
  function onDisconnect(): void {
    const store = conversation.getState();
    if (!store.isStreaming) return;

    log.warn('Socket disconnected during active conversation — stalling stream');

    tts.cancel();

    // Remember the interrupted message + session for auto-retry
    const aiMsg = store.messages.find((m) => m.role === 'assistant' && m.status === 'streaming');
    if (aiMsg && store.currentSessionId) {
      interruptedStream = { aiMessageId: aiMsg.id, sessionId: store.currentSessionId };
    }

    // Commit partial text, clear streaming state. No error UI.
    conversation.setState((s) => ({
      messages: s.messages.map((m) => {
        if (m.role === 'assistant' && m.status === 'streaming') {
          return { ...m, text: s.streamingText || m.text || '', status: 'complete' as MessageStatus };
        }
        if (m.role === 'user' && m.status === 'sending') {
          return { ...m, status: 'complete' as MessageStatus, analysisPending: false };
        }
        return m;
      }),
      isStreaming: false,
      isSendingMessage: false,
      isAnalyzing: false,
      streamingText: '',
      streamingMessageId: null,
    }));
  }

  /** Called when socket transitions from disconnected → connected */
  function onReconnect(): void {
    if (!interruptedStream) return;

    const { aiMessageId, sessionId } = interruptedStream;
    interruptedStream = null;

    const store = conversation.getState();

    if (store.currentSessionId !== sessionId) {
      log.info('Skipping auto-retry — session changed', { was: sessionId, now: store.currentSessionId });
      return;
    }
    if (!store.messages.some((m) => m.id === aiMessageId)) {
      log.info('Skipping auto-retry — message no longer in store', { aiMessageId });
      return;
    }
    if (store.isStreaming) {
      log.info('Skipping auto-retry — already streaming', { aiMessageId });
      return;
    }

    log.info('Socket reconnected — auto-retrying interrupted conversation', { aiMessageId, sessionId });

    scheduleRetry(() => {
      conversation.retryConversation(aiMessageId).catch((err) => {
        log.error('Auto-retry after reconnect failed', err);
      });
    });
  }

  /** Get the current interrupted stream state (for testing) */
  function getInterruptedStream(): InterruptedStream | null {
    return interruptedStream;
  }

  /** Clear the interrupted stream (e.g., when user manually retries or leaves) */
  function clearInterruptedStream(): void {
    interruptedStream = null;
  }

  return { onDisconnect, onReconnect, getInterruptedStream, clearInterruptedStream };
}
